/**
 * Workspace context — multi-tenant isolation on the client.
 *
 * The active workspace is stored in localStorage and attached to every API
 * call via the `X-Workspace-Id` header (see lib/api.ts safeFetch). Switching
 * workspaces updates this value and the app reloads its context (memberships,
 * branding, navigation).
 */

export const WORKSPACE_STORAGE_KEY = 'activeWorkspaceId';

export interface WorkspaceBranding {
  primary?: string;
  primaryLight?: string;
  primaryDark?: string;
  accent?: string;
  textLight?: string;
  logoUrl?: string;
  sidebarLogoUrl?: string;
  faviconUrl?: string;
  businessName?: string;
  tagline?: string;
}

export interface Workspace {
  workspaceId: string;
  slug: string;
  name: string;
  orgName: string;
  role?: string;
  plan: string;
  status: string;
  branding?: WorkspaceBranding;
  customDomains?: string[];
  emailSender?: { name?: string; email?: string };
}

export interface WorkspacesMe {
  memberships: Workspace[];
  activeWorkspaceId: string;
  platformAdmin: boolean;
  workspace: Workspace | null;
  /** Workspace explicitly requested via the URL subdomain / header (no fallback). */
  requestedWorkspaceId?: string | null;
  requestedSlug?: string | null;
  /** True when the authenticated user is a member of the requested workspace. */
  isRequestedMember?: boolean;
}

export function getActiveWorkspaceId(): string {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setActiveWorkspaceId(id: string): void {
  try {
    localStorage.setItem(WORKSPACE_STORAGE_KEY, id);
  } catch { /* ignore */ }
}

export function workspaceHeader(): Record<string, string> {
  const id = getActiveWorkspaceId();
  return id ? { 'X-Workspace-Id': id } : {};
}

export function isSameWorkspaceSync(id1?: string | null, id2?: string | null): boolean {
  if (!id1 || !id2) return true;
  const k1 = id1.trim().toLowerCase();
  const k2 = id2.trim().toLowerCase();
  return k1 === k2;
}
